import type { TOC } from '@ember/component/template-only';
import { tmdbProviderLogo } from '../../utils/external-urls';

export interface WatchProvider {
  providerId: number;
  providerName: string;
  logoPath: string;
}

export interface WatchProviderRegion {
  link?: string;
  flatrate?: WatchProvider[];
  rent?: WatchProvider[];
  buy?: WatchProvider[];
}

interface Signature {
  Args: {
    providers: Record<string, WatchProviderRegion> | null | undefined;
    region?: string;
    subscribedServices?: number[];
  };
}

function getRegionProviders(
  providers: Record<string, WatchProviderRegion> | null | undefined,
  region: string | undefined
): WatchProviderRegion | null {
  if (!providers) return null;
  const code = region || 'US';
  return providers[code] ?? null;
}

function sortProviders(
  providers: WatchProvider[] | undefined,
  subscribed: number[] | undefined
): WatchProvider[] {
  if (!providers) return [];
  if (!subscribed || subscribed.length === 0) return providers;
  const set = new Set(subscribed);
  return [...providers].sort((a, b) => {
    const aSubscribed = set.has(a.providerId) ? 0 : 1;
    const bSubscribed = set.has(b.providerId) ? 0 : 1;
    return aSubscribed - bSubscribed;
  });
}

function isSubscribed(
  providerId: number,
  subscribed: number[] | undefined
): boolean {
  if (!subscribed || subscribed.length === 0) return false;
  return subscribed.includes(providerId);
}

function hasProviders(region: WatchProviderRegion | null): boolean {
  if (!region) return false;
  return (
    (region.flatrate?.length ?? 0) > 0 ||
    (region.rent?.length ?? 0) > 0 ||
    (region.buy?.length ?? 0) > 0
  );
}

function hasFlatrate(region: WatchProviderRegion | null): boolean {
  return (region?.flatrate?.length ?? 0) > 0;
}

function hasRent(region: WatchProviderRegion | null): boolean {
  return (region?.rent?.length ?? 0) > 0;
}

function hasBuy(region: WatchProviderRegion | null): boolean {
  return (region?.buy?.length ?? 0) > 0;
}

function hasLink(region: WatchProviderRegion | null): boolean {
  return Boolean(region?.link);
}

function tmdbWatchLink(region: WatchProviderRegion | null): string {
  return region?.link ?? '';
}

function hasAnyRegionProviders(
  providers: Record<string, WatchProviderRegion> | null | undefined
): boolean {
  if (!providers) return false;
  return Object.values(providers).some(
    (r) =>
      (r.flatrate?.length ?? 0) > 0 ||
      (r.rent?.length ?? 0) > 0 ||
      (r.buy?.length ?? 0) > 0
  );
}

function getFallbackTmdbLink(
  providers: Record<string, WatchProviderRegion> | null | undefined,
  region: string | undefined
): string {
  if (!providers) return '';
  const first = Object.values(providers).find((r) => r.link);
  const link = first?.link ?? '';
  if (!link || !region) return link;
  return link.replace(/([?&]locale=)[A-Z]{2}/i, `$1${region}`);
}

function subscribedClass(
  providerId: number,
  subscribed: number[] | undefined
): string {
  return isSubscribed(providerId, subscribed)
    ? 'ring-2 ring-primary ring-offset-1'
    : '';
}

/**
 * Displays streaming/rental/purchase providers for a movie or TV show.
 * Shows provider logos with names, grouped by availability type.
 * Subscribed services (from user preferences) are sorted first and highlighted.
 */
const WatchProviders: TOC<Signature> = <template>
  {{#let (getRegionProviders @providers @region) as |region|}}
    {{#if (hasProviders region)}}
      <div class="flex flex-col gap-2">
        <h4
          class="text-base-content/70 text-xs font-semibold tracking-wide uppercase"
        >
          Where to Watch
        </h4>

        {{#if (hasFlatrate region)}}
          <div class="flex flex-wrap gap-2">
            {{#each
              (sortProviders region.flatrate @subscribedServices)
              as |provider|
            }}
              {{#if (hasLink region)}}
                <a
                  href={{tmdbWatchLink region}}
                  target="_blank"
                  rel="noopener noreferrer"
                  class="tooltip bg-base-200 hover:bg-base-300 flex items-center gap-1.5 rounded-lg px-2 py-1.5 transition-colors
                    {{subscribedClass provider.providerId @subscribedServices}}"
                  data-tip={{provider.providerName}}
                >
                  <img
                    src={{tmdbProviderLogo provider.logoPath}}
                    alt={{provider.providerName}}
                    class="h-6 w-6 rounded"
                    loading="lazy"
                  />
                  <span
                    class="text-xs font-medium"
                  >{{provider.providerName}}</span>
                </a>
              {{else}}
                <span
                  class="tooltip bg-base-200 flex items-center gap-1.5 rounded-lg px-2 py-1.5
                    {{subscribedClass provider.providerId @subscribedServices}}"
                  data-tip={{provider.providerName}}
                >
                  <img
                    src={{tmdbProviderLogo provider.logoPath}}
                    alt={{provider.providerName}}
                    class="h-6 w-6 rounded"
                    loading="lazy"
                  />
                  <span
                    class="text-xs font-medium"
                  >{{provider.providerName}}</span>
                </span>
              {{/if}}
            {{/each}}
          </div>
        {{/if}}

        {{#if (hasRent region)}}
          <div class="flex flex-wrap items-center gap-2">
            <span class="text-base-content/50 text-xs">Rent</span>
            {{#each
              (sortProviders region.rent @subscribedServices)
              as |provider|
            }}
              {{#if (hasLink region)}}
                <a
                  href={{tmdbWatchLink region}}
                  target="_blank"
                  rel="noopener noreferrer"
                  class="tooltip
                    {{subscribedClass provider.providerId @subscribedServices}}"
                  data-tip={{provider.providerName}}
                >
                  <img
                    src={{tmdbProviderLogo provider.logoPath}}
                    alt={{provider.providerName}}
                    class="h-6 w-6 rounded"
                    loading="lazy"
                  />
                </a>
              {{else}}
                <span
                  class="tooltip
                    {{subscribedClass provider.providerId @subscribedServices}}"
                  data-tip={{provider.providerName}}
                >
                  <img
                    src={{tmdbProviderLogo provider.logoPath}}
                    alt={{provider.providerName}}
                    class="h-6 w-6 rounded"
                    loading="lazy"
                  />
                </span>
              {{/if}}
            {{/each}}
          </div>
        {{/if}}

        {{#if (hasBuy region)}}
          <div class="flex flex-wrap items-center gap-2">
            <span class="text-base-content/50 text-xs">Buy</span>
            {{#each
              (sortProviders region.buy @subscribedServices)
              as |provider|
            }}
              {{#if (hasLink region)}}
                <a
                  href={{tmdbWatchLink region}}
                  target="_blank"
                  rel="noopener noreferrer"
                  class="tooltip
                    {{subscribedClass provider.providerId @subscribedServices}}"
                  data-tip={{provider.providerName}}
                >
                  <img
                    src={{tmdbProviderLogo provider.logoPath}}
                    alt={{provider.providerName}}
                    class="h-6 w-6 rounded"
                    loading="lazy"
                  />
                </a>
              {{else}}
                <span
                  class="tooltip
                    {{subscribedClass provider.providerId @subscribedServices}}"
                  data-tip={{provider.providerName}}
                >
                  <img
                    src={{tmdbProviderLogo provider.logoPath}}
                    alt={{provider.providerName}}
                    class="h-6 w-6 rounded"
                    loading="lazy"
                  />
                </span>
              {{/if}}
            {{/each}}
          </div>
        {{/if}}

        <p class="text-base-content/40 text-xs">
          Powered by
          <a
            href="https://www.justwatch.com"
            target="_blank"
            rel="noopener noreferrer"
            class="underline"
          >JustWatch</a>
        </p>
      </div>
    {{else if (hasAnyRegionProviders @providers)}}
      <div class="flex flex-col gap-2">
        <h4
          class="text-base-content/70 text-xs font-semibold tracking-wide uppercase"
        >
          Where to Watch
        </h4>
        <a
          href={{getFallbackTmdbLink @providers @region}}
          target="_blank"
          rel="noopener noreferrer"
          class="text-base-content/60 hover:text-base-content text-xs underline"
        >
          Not available in your region — check availability on TMDB ↗
        </a>
        <p class="text-base-content/40 text-xs">
          Powered by
          <a
            href="https://www.justwatch.com"
            target="_blank"
            rel="noopener noreferrer"
            class="underline"
          >JustWatch</a>
        </p>
      </div>
    {{/if}}
  {{/let}}
</template>;

export default WatchProviders;
